/* eslint-disable max-len */
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog as MuiDialog,
} from "@mui/material";
import { EtAuction, LandToken } from "../../types/LandToken";

import { ecoFuturesBankContractAddress, getContract, getMetaMaskSigner } from "../../services/contract.service";
import { fromWei, toWei } from "web3-utils";

import useAuthorization from "../../contexts/useAuthorization";
import moment from "moment";
import { toBigInt } from "ethers";
import { CURRENCY_NAME, DATE_TIME_FORMAT, TIME_DISPLAY_UNIT, TERM_UNIT } from "../../utils/constants";
import { UserRoles } from "../../types/User";
import { checkTransactionConfirmationReceipt, sixDigits, sizing, sleep, toReadable } from "../../utils/methods";
import { BidSchema, ClickableContainer, ProvenanceMobileSchema, ProvenanceSchema, timeCol } from "../../utils/table-schemas";
import {
  Text,
  AddressLink,
  CountdownTimer,
  CurrencyLabel,
  TableComponent,
  PulseCircle,
  ActionBtn,
  TableSchema,
} from "../../components";
import { Attention } from "../BecomeTrust";
import { formatValue } from "../../utils/formatValue";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TabsRow } from "../EcoBank/TrustWallet";
import { TableWithTabs } from "../EcoBank/UserWallet";
import { Row } from "../../components/FlexComponents";
import { FilterBlock } from "../Profile/GuardianPortal";
import Easement from "./Easement";
import LandActionPopup, { PopupCTA } from "./LandActionPopup";
import ArrowForwardSharpIcon from "@mui/icons-material/ArrowForwardSharp";
import { AppContext } from "../../contexts";

type AuctionProps = {
  token: LandToken;
  compact?: boolean;
  details?: boolean;
  fetchToken?(): any;
  mintCapacity?: any;
  isOwner?: boolean;
  isAuctionLoading?: boolean;
  setIsAuctionLoading?: (isAuctionLoading: boolean) => void;
  mintPerDay?: boolean;
  onlyOffers?: boolean;
  onlyEasements?: boolean;
  onlyProvenance?: boolean;
  auctionData?: any;
  offersData?: any[];
  transactionsData?: any[];
  transactionsCountData?: number;
};

export type AuctionDetails = [
  currency: any,
  seller: any,
  startingPrice: any,
  endingPrice: any,
  currentPrice: any,
  highestBidder: any,
  onAuction: boolean,
  bidderSize: any,
  startingTime: any,
  duration: any
];

type BidState = "initial" | "bidding" | "processing";

const Auction = ({
  token,
  compact,
  fetchToken,
  isOwner,
  setIsAuctionLoading,
  mintPerDay,
  onlyOffers,
  onlyEasements,
  onlyProvenance,
  auctionData,
  offersData,
  transactionsData,
  transactionsCountData,
}: AuctionProps) => {
  // const axios = useAxios();
  const { user } = useAuthorization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(560));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const provenanceRef = useRef<HTMLDivElement>(null);

  const [nextBidValid, setNextBidValid] = useState(false);
  const [balance, setBalance] = useState<string>("0");
  const [nextBid, setNextBid] = useState<string>("");
  const [currentBid, setCurrentBid] = useState<string | number>("0");
  const [activeTab, setActiveTab] = useState<string>("provenance");

  const [biddingModal, setBiddingModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);

  const [auctionLoading, setAuctionLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<number | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startingPrice, setStartingPrice] = useState<string>("");

  const [remainingTrees, setRemainingTrees] = useState<any>(0);
  const [treesValue, setTreesValue] = useState<any>(0);
  const [auctionEnded, setAuctionEnded] = useState<boolean>(false);

  const [auctionStatus, setAuctionStatus] = useState<EtAuction["status"] | null>(null);

  const [endingInProcess, setEndingProcess] = useState<boolean>(false);
  const [winnerAddress, setWinnerAddress] = useState<string | null>("");

  const [sellerAddress, setSellerAddress] = useState<string>("");
  const [highestBidderAddress, setHighestBidderAddress] = useState<string>("");
  // const [bidList, setBidList] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [infoMessage, setInfoMessage] = useState<string>("");
  const [auctionEndTs, setAuctionEndTs] = useState<string>("");

  const [canBid, setCanBid] = useState<boolean>(false);
  const [txnLoading, setTxnLoading] = useState<boolean>(false);
  const [hasAuction, setHasAuction] = useState<boolean>(false);

  const [hasBids, setHasBids] = useState<boolean>(false);

  const [transactionsCount, setTransactionsCount] = useState(0);
  const [startedRecently, setStartedRecently] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [biddingTiers, setBiddingTiers] = useState<any[]>([]);
  const [offerList, setOfferList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentBids, setCurrentBids] = useState<any[]>([]);
  // const [highestOffer, setHighestOffer] = useState<any>(0);
  // const [bidsLoading, setBidsLoading] = useState<boolean>(false);
  const appContext = useContext(AppContext);
  // Track which offer is being accepted
  const [acceptingOffer, setAcceptingOffer] = useState<string | null>(null);

  const [bidState, setBidState] = useState<BidState>("initial");

  const bidData = {
    tier: (
      <>
        {(() => {
          if (!biddingTiers.length) {
            return "Tier 1";
          }

          const currentBidNum = typeof currentBid === "string" ? parseFloat(currentBid) : currentBid;

          // Sort tiers by price ascending
          const sortedTiers = [...biddingTiers].sort((a, b) => {
            const priceA = typeof a.price === "string" ? parseFloat(a.price) : a.price;
            const priceB = typeof b.price === "string" ? parseFloat(b.price) : b.price;
            return priceA - priceB;
          });

          // Find the next tier that the current bid hasn't reached yet
          for (const tier of sortedTiers) {
            const tierPrice = typeof tier.price === "string" ? parseFloat(tier.price) : tier.price;
            if (currentBidNum < tierPrice) {
              return tier.title;
            }
          }

          // If no next tier found, use the highest tier
          return sortedTiers[sortedTiers.length - 1]?.title || "Tier 1";
        })()}
      </>
    ),
    tierPrice: (() => {
      if (!biddingTiers.length) {
        return "0 USD";
      }

      const currentBidNum = typeof currentBid === "string" ? parseFloat(currentBid) : currentBid;

      // Sort tiers by price ascending
      const sortedTiers = [...biddingTiers].sort((a, b) => {
        const priceA = typeof a.price === "string" ? parseFloat(a.price) : a.price;
        const priceB = typeof b.price === "string" ? parseFloat(b.price) : b.price;
        return priceA - priceB;
      });

      // Find the next tier that the current bid hasn't reached yet
      for (const tier of sortedTiers) {
        const tierPrice = typeof tier.price === "string" ? parseFloat(tier.price) : tier.price;
        if (currentBidNum < tierPrice) {
          return `${formatValue(tier.price, true, 2)} USD`;
        }
      }

      // If no next tier found, use the highest tier price
      const highestTier = sortedTiers[sortedTiers.length - 1];
      return highestTier ? `${formatValue(highestTier.price, true, 2)} USD` : "0 USD";
    })(),
    mintCapacity: (
      <>
        {token?.etNft?.totalTreeForEasement ? formatValue(token.etNft.totalTreeForEasement, true, 3) + " TREEs" : "N/A"}
      </>
    ),
    contractPeriod: (
      <>
        {token.meta?.contractPeriod} {TERM_UNIT.toUpperCase()}
      </>
    ),
    currentBid: <>{formatValue(currentBid)} USD</>,
    availableBalance: <>{balance} USD</>,
  };

  useEffect(() => {
    if (location.hash === "#provenance" && provenanceRef.current) {
      provenanceRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash, provenanceRef.current]);

  useEffect(() => {
    if (setIsAuctionLoading) {
      if (auctionLoading || endingInProcess) {
        setIsAuctionLoading(true);
      } else {
        setIsAuctionLoading(false);
      }
    }
  }, [auctionLoading, endingInProcess]);

  // const BidValues = [
  //   {
  //     "Mint capacity": <>{token?.etNft?.totalTreeForEasement ?
  //       formatValue(token.etNft.totalTreeForEasement, true, 3)
  //       : "N/A"}
  //       < CurrencyLabel /></>
  //   },
  //   { "Contract period": <>{token.meta?.contractPeriod} <CurrencyLabel currency={TERM_UNIT} /></> },
  //   { "Opening bid": <>{formatValue(token.meta?.startingBid)} <CurrencyLabel currency={CURRENCY_NAME} /></> },
  //   //  { "Current bid": <>{formatValue(currentBid)} <CurrencyLabel currency={CURRENCY_NAME} /></> },
  //   // { "Highest bidder": <><AddressLink sx={{ fontSize: "18px" }} type="user" address={highestBidderAddress} links={false} /></> },
  //   { "Available balance": <> {balance} <CurrencyLabel currency={CURRENCY_NAME} /></> },

  // ];

  useEffect(() => {
    if (token.meta) {
      setBiddingTiers([
        {
          title: "Tier 1",
          description: token.meta.bfd_plan,
          perks: token.meta.basicPerks,
          price: token.meta.startingBid,
        },
      ]);
      if (token?.easements?.length) {
        token.easements.forEach((easement) => {
          setBiddingTiers((prev) => [
            ...prev,
            {
              title: `Tier ${easement.level + 1}`,
              description: easement.description,
              perks: easement.perks,
              price: easement.activationBid,
            },
          ]);
        });
      }
    }
  }, []);

  useEffect(() => {
    if (compact && endDate && startDate) {
      const checkTime = () => {
        const timeLeft = (endDate as Date).getTime() - Date.now();
        const min = 60 * 1000;
        const activeTime = Date.now() - startDate;
        if (activeTime <= 2 * min) {
          if (!startedRecently) {
            setStartedRecently(true);
          }
        } else {
          if (startedRecently) {
            setStartedRecently(false);
          }
        }

        if (timeLeft <= 0 && !isExpired) {
          setIsExpired(true);
        }

        if (timeLeft <= 2 * min && !isDisabled) {
          setIsDisabled(true);
        }

        const duration = moment.duration(timeLeft, "milliseconds");
        if (duration.asMinutes() < 3) {
          console.log(
            `Auction '${auctionStatus}' ending in ` +
            duration.minutes().toFixed() +
            "m " +
            duration.seconds().toFixed() +
            "s"
          );
        }
      };

      const interval = setInterval(checkTime, 1000);

      if (isExpired && auctionStatus === "started" && !endingInProcess) {
        setEndingProcess(true);
        if (fetchToken) {
          fetchToken().then(() => fetchAuctionInfo());
        }
      }

      if (isExpired && (auctionStatus === "completed" || auctionStatus === "ended")) {
        if (auctionStatus === "completed") {
          if (endingInProcess) {
            setEndingProcess(false);
          }
          // else if (!endingInProcess) {
          //   setEndingProcess(true);
          // }
        } else {
          if (winnerAddress) {
            if (endingInProcess) {
              setEndingProcess(false);
            }
          } else {
            if (endingInProcess) {
              setEndingProcess(false);
            }
          }
        }

        clearInterval(interval);
      }

      if (auctionStatus === "cancelled") {
        if (endingInProcess) {
          setEndingProcess(false);
        }
        clearInterval(interval);
      }

      return () => clearInterval(interval);
    }
    return () => null;
  }, [endDate, isExpired, isDisabled, auctionStatus, endingInProcess]);

  const getMintRate = () => {
    const total = formatValue(token.etNft?.totalTreeForEasement as any, false);
    const days = moment.duration(token.meta?.contractPeriod, TIME_DISPLAY_UNIT).as("days");
    return formatValue(total / (days - 1), true, 3);
  };

  useEffect(() => {
    if (compact) {
      fetchAuctionInfo();
    } else {
      fetchTransactions();
      fetchAuctionInfo();
    }
  }, []);

  useEffect(() => {
    validateNextBid(nextBid);
  }, [nextBid]);

  useEffect(() => {
    switch (true) {
      case user?.role === UserRoles.TRUST:
        setErrorMessage("You cannot place bid from trust account.");
        setCanBid(false);
        break;

      case sellerAddress === user?.address:
        setErrorMessage("Auction owner cannot place bid.");
        setCanBid(false);
        break;

      case token?.ownerAddress === user?.address:
        setErrorMessage("Auction owner cannot place bid.");
        setCanBid(false);
        break;

      default:
        setErrorMessage("");
        setCanBid(true);
    }
  }, [sellerAddress, user?.address, user?.role, token?.ownerAddress]);

  // useEffect(() => {
  //   fetchAuctionInfo();
  // }, [activeTab]);

  const fetchTransactions = () => {
    try {
      if (token.offerStatus === "opened") {
        // Only fetch offers if offersData is null or empty
        const offers = offersData;
        // if (!offersData || offersData.length === 0) {
        //   offers = await axios.get(ApiRoutes.token.offers(token?.id as string)).then((res) => res.data);
        // }

        if (Array.isArray(offers) && offers.length > 0) {
          // const highest = offers.reduce((max, offer) =>
          //   Number(offer.amount) > Number(max.amount) ? offer : max, offers[0]
          // );
          // setHighestOffer(Number(highest?.amount));
          setOfferList(offers);
        } else {
          setOfferList([]);
        }
      }
      if (token.etNft?.id) {
        const transactionsNumber = transactionsCountData || transactionsData?.length || 0;
        // const result = await axios
        //   .get(ApiRoutes.ecoBank.transactions.byNft(token.etNft?.id as number) + "&total=true")
        //   .then((res) => res.data);
        setTransactionsCount(transactionsNumber);
      }
    } catch (e) {
      console.log("Failed to fetch transactions", { e });
    }
  };

  const fetchBids = async (): Promise<EtAuction | false> => {
    try {
      if (token.etNft) {
        const bids = auctionData || [];
        console.log("Fetched bids:", bids);
        // const result = await axios
        //   .get(ApiRoutes.token.auction.getInfo(token.etNft?.id as number))
        //   .then((res) => res.data);
        if (bids?.bids?.length > 0) {
          setHasBids(true);
        }
        console.log("Bids for auction:", bids);
        console.log("Bids array:", bids.bids);
        console.log("Bids Transactions:", bids.bidTransactions);
        console.log("Bids count:", bids.bids?.length);
        // const transactionDataBids = transactionsData?.filter((transaction) => transaction.type === "bid");
        // console.log("Transaction data bids:", transactionDataBids);
        setCurrentBids(bids.bidTransactions || []);
        // Get transactions and filter bid transactions

        // const transactionsResponse = transactionsData || []; // Use transactionsData prop directly
        // If transactionsData is not provided, uncomment the following line to fetch from API
        // const transactionsResponse = await axios
        //   .get(ApiRoutes.ecoBank.transactions.byNft(token?.etNft?.id as number));

        // if (transactionsResponse && Array.isArray(transactionsResponse)) {
        //   // Filter transactions with type "bid" and sort by date descending
        //   console.log("Fetched transactions:", transactionsResponse);
        //   const bidTransactions = transactionsResponse
        //     .filter((transaction) => transaction.type === "bid")
        //     .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        //   console.log("Filtered bid transactions:", bidTransactions);
        //   setCurrentBids(bidTransactions);
        // }

        return bids;
      }

      return false;
    } catch (e) {
      console.log("Failed to fetch bids", { e });
      return false;
    }
  };

  const fetchAuctionInfo = async () => {
    try {
      setAuctionLoading(true);
      const bidsRes = await fetchBids();
      if (!bidsRes) {
        setAuctionLoading(false);
      } else {
        const { auctionStart, endingPrice, startingPrice, auctionEnd, status, winner, duration, bids } = bidsRes;

        const currentBid = formatValue(endingPrice, false);

        if (bids?.length > 0) {
          setHighestBidderAddress(bids[bids.length - 1]?.bidder as any);
        }

        setStartingPrice(fromWei(startingPrice, "ether"));
        setCurrentBid(currentBid);
        setSellerAddress(token.minterAddress as string);

        const startedTs = moment(auctionStart).valueOf();
        const endTime = moment(startedTs + duration);

        setStartDate(startedTs);
        setEndDate(endTime.toDate());
        setAuctionStatus(status);

        if (status === "sold" || status === "ended" || status === "completed" || status === "cancelled") {
          setAuctionEnded(true);

          console.log("Auction status is - " + status);
          const { etNft } = token;
          if (etNft?.totalTreeForEasement) {
            console.log("Contract activated");
            const total = formatValue(etNft?.totalTreeForEasement, false);
            const claimed = formatValue(
              etNft?.easementActivated?.rewardClaimed ? etNft?.easementActivated.rewardClaimed : 0,
              false
            );
            const remaining = total - (claimed ? claimed : 0) > 0 ? total - (claimed ? claimed : 0) : 0;
            const rate = appContext?.mintRate;
            console.log("Remaining trees", remaining, "Rate", rate);
            if (rate) {
              const balance = formatValue(remaining * rate, true, 2);
              const valueText = `(@ $${formatValue(rate, true, 4)} / TREE)`;
              setRemainingTrees(balance);
              setTreesValue(valueText);
              console.log("Remaining trees", treesValue);
            } else {
              setRemainingTrees(remaining);
            }
          }

          const _winner = winner || etNft?.minterAddress !== etNft?.ownerAddress ? etNft?.ownerAddress : "";
          if (!winner && status === "completed" && user.address === token.ownerAddress) {
            handleCancelAuction();
            console.log("Auction completed but winner is not set, cancelling auction", _winner);
          } else if (!winner) {
            setHasAuction(true);
            console.log("Winner is not set, using owner address", _winner);
            if (status === "ended" || status === "cancelled" || status === "completed") {
              if (endingInProcess) {
                setEndingProcess(false);
                if (fetchToken) {
                  await fetchToken();
                }
              }
            } else {
              setEndingProcess(true);
            }
            setAuctionEndTs(moment(auctionEnd).format(DATE_TIME_FORMAT));
            await fetchTransactions();
            setTimeout(() => {
              setAuctionLoading(false);
            }, 1000);
            return;
          } else {
            setHasAuction(true);
            setEndingProcess(false);
            setWinnerAddress(_winner || null);
            setAuctionEndTs(moment(auctionEnd).format(DATE_TIME_FORMAT));
            await fetchTransactions();
            setTimeout(() => {
              setAuctionLoading(false);
            }, 1000);
          }
        } else {
          console.log("Auction status is - " + status);
          await fetchTransactions();
          setHasAuction(true);
          setTimeout(() => {
            setAuctionLoading(false);
          }, 1000);
        }
      }
    } catch (e: any) {
      console.log({ error: e });
      setAuctionLoading(false);
    } finally {
      setAuctionLoading(false);
      if (user) {
        try {
          const signer = await getMetaMaskSigner();
          const ercContract = getContract("erc20", signer);
          if (ercContract) {
            const ercBalance = await ercContract.balanceOf(user?.address);
            setBalance(toReadable(ercBalance.toString()).toString());
          }
        } catch (error) {
          console.error("Error fetching balance:", error);
        }
      }
    }
  };

  const validateNextBid = (value: string) => {
    setNextBid(value);
    const num = Number(value);
    if (num <= 0) {
      setNextBidValid(false);
    } else if (num > 0 && value.startsWith("0") && !value.includes(".")) {
      setNextBidValid(false);
    } else if (num > Number(balance)) {
      setNextBidValid(false);
    } else if (num < Number(currentBid)) {
      setNextBidValid(false);
    } else if (num === Number(currentBid) && Number(currentBid) !== Number(startingPrice)) {
      setNextBidValid(false);
    } else if (value.includes("e") || value.includes("-")) {
      setNextBidValid(false);
    } else {
      setNextBidValid(true);
    }
  };

  const submitApproval = async (bid: BigInt | string | number) => {
    try {
      const signer = await getMetaMaskSigner();
      const erc20Contract = getContract("erc20", signer);
      if (erc20Contract) {
        setTxnLoading(true);

        // Exact-scope approval: the Bank pulls the winning bid's escrow at endAuction,
        // so the allowance only needs to cover this bid. Read the current allowance and
        // top up just the shortfall — re-bids and retries no longer stack approvals.
        const owner = await signer.getAddress();
        const currentAllowance: bigint = await erc20Contract.allowance(owner, ecoFuturesBankContractAddress);
        const required = toBigInt(bid as any);
        if (currentAllowance >= required) {
          return true;
        }

        setInfoMessage("Requesting spending approval...");
        const result = await erc20Contract.increaseAllowance(
          ecoFuturesBankContractAddress,
          required - currentAllowance
        );

        setInfoMessage("Awaiting approval request transaction...");
        const txnDone = await checkTransactionConfirmation(result.hash, true);
        if (txnDone) {
          return true;
        } else {
          throw new Error("ERC20 Approval wasn't granted. Transaction failed");
        }
      } else {
        throw new Error("ERC20 Approval wasn't granted. Contract is not initialized");
      }
    } catch (e) {
      console.log("Approval error", { error: e });
      setInfoMessage("Failed spending approval request!");
      setBidState("initial");
      return false;
    }
  };

  const submitBid = async () => {
    if (nextBidValid && Number(nextBid) > 0) {
      try {
        setBidState("processing");
        const signer = await getMetaMaskSigner();
        const auctionContract = getContract("ecofutures-bank", signer);
        if (auctionContract) {
          const bidInWei = toWei(nextBid.toString(), "ether");
          const bid = toBigInt(bidInWei);
          const approved = await submitApproval(bid);
          if (!approved) {
            throw new Error("Not approved spending");
          }

          await new Promise((resolve) => {
            setTimeout(() => {
              resolve("ok");
            }, 2000);
          });

          setInfoMessage("Requesting bidding approval...");

          setTxnLoading(true);
          console.log(bid, token.etNft?.etAuction.nftId);
          const bidResult = await auctionContract.bid(token.etNft?.etAuction.nftId, bid);

          setInfoMessage("Awaiting bidding transaction...");

          const txnRes = await checkTransactionConfirmation(bidResult.hash, false);
          if (txnRes) {
            setInfoMessage("Bid placed!");
            await new Promise((resolve) => {
              setTimeout(() => {
                resolve("ok");
              }, 2000);
            });
            await fetchAuctionInfo();

            setTxnLoading(false);
            handleCloseBidding();
          }
        } else {
          console.error("Contract not initialized");
        }
      } catch (e) {
        setInfoMessage("");
        setErrorMessage("Failed to submit bid. Try again later.");

        setTimeout(() => {
          setTxnLoading(false);
        }, 1000);
        console.error("Submit Bid Error", { error: e });
      }
    }
  };

  const handleCloseBidding = () => {
    setNextBid("");
    setInfoMessage("");
    setErrorMessage("");
    setBiddingModal(false);
    window.location.reload();
  };

  const handleCloseConfirm = () => {
    setConfirmModal(false);
  };

  const handleCancelAuction = async () => {
    try {
      setInfoMessage("Requesting auction cancelling...");
      const signer = await getMetaMaskSigner();
      const contract = getContract("ecofutures-bank", signer);
      console.log("Contract", contract);

      if (contract) {
        setTxnLoading(true);

        const result = await contract.cancelAuction(token.etNft?.id);
        setInfoMessage("Waiting for transaction to complete...");

        const txnRes = await checkTransactionConfirmation(result.hash, false);
        if (txnRes) {
          setInfoMessage("Auction cancelled successfully");

          setTimeout(async () => {
            await sleep(7000);
            if (fetchToken) {
              await fetchToken();
            }
            await fetchAuctionInfo();
            setTxnLoading(false);
            setInfoMessage("");
            handleCloseConfirm();
          }, 1500);
        }
      } else {
        console.error("Contract not initialized");
      }
    } catch (e) {
      console.log({ error: e });
      setInfoMessage("Failed to cancel auction. Try again later.");
      setTimeout(() => {
        setInfoMessage("");
        setTxnLoading(false);
      }, 2000);
    }
  };

  // const handleEndAuction = async () => {
  //   try {
  //     const signer = await getMetaMaskSigner();
  //     const ecoFuturesBankContract = getContract("ecofutures-bank", signer);
  //     console.log("End auction contract", ecoFuturesBankContract);
  //     if (ecoFuturesBankContract) {
  //       setTxnLoading(true);

  //       const result = await ecoFuturesBankContract.endAuction(token.etNft?.etAuction.nftId);
  //       const txnRes = await checkTransactionConfirmation(result.hash, false);
  //       console.log("End auction result", { result, txnRes });
  //       setTxnLoading(false);
  //       if (txnRes) {
  //         await fetchAuctionInfo();
  //         setTimeout(() => {
  //           handleCloseBidding();
  //         }, 1000);
  //       }
  //     } else {
  //       console.error("Contract not initialized");
  //     }

  //     handleCloseConfirm();
  //   } catch (e) {
  //     console.log({ error: e });
  //     setTxnLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   console.log("Auction status changed:", auctionData?.status, "Winner address:", auctionData?.winner);
  //   if (auctionData?.status === "completed" && !auctionData?.winner) {
  //     console.log("Auction completed but winner address is null, handling end auction");
  //     handleEndAuction();
  //   }
  // }, [auctionData]);

  const checkTransactionConfirmation = async (hash: string, _bidding: boolean) => {
    try {
      const checkTransactionLoop = async (): Promise<boolean> => {
        try {
          const receipt = await window.ethereum.request({
            method: "eth_getTransactionReceipt",
            params: [hash],
          });

          if (receipt != null) {
            return true;
          } else {
            // Wait a bit before checking again to avoid hammering the provider
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return await checkTransactionLoop();
          }
        } catch (error) {
          console.error("Error checking transaction receipt:", error);
          return false;
        }
      };

      return await checkTransactionLoop();
    } catch (e) {
      console.log({ error: e });
      return false;
    }
  };

  const handleAcceptOffer = async (tokenId: number, offerIndex: number, buyer: string, amount: number) => {
    const offerKey = `${tokenId}-${offerIndex}`;
    setAcceptingOffer(offerKey);
    setIsLoading(true);
    setErrorMessage("");
    setInfoMessage("");
    try {
      const signer = await getMetaMaskSigner();
      const contract = getContract("ecofutures-token", signer);
      if (contract) {
        const result = await contract.acceptOffer(tokenId, offerIndex, buyer, amount); //tokenID & offerIndex
        console.log({ result });
        // Use the checkTransactionConfirmationReceipt without passing library
        const r = await checkTransactionConfirmationReceipt(result.hash);
        console.log({ r });
        const receipt = r as any;
        if (receipt?.status !== "0") {
          console.log("NFT offer accepted");
          setInfoMessage("NFT offer accepted");
          window.location.reload();
        }
      }
    } catch (error: any) {
      console.log({ error });
      // Handle MetaMask rejection
      if (error.code === 4001 || error.message?.includes("User rejected") || error.code === "ACTION_REJECTED") {
        setErrorMessage("Transaction was rejected by user");
        return;
      }
      setErrorMessage("Failed to accept offer");
    } finally {
      setIsLoading(false);
      setAcceptingOffer(null);
    }
  };

  const handleWithdrawOffer = async (tokenId: number, offerIndex: number) => {
    const offerKey = `${tokenId}-${offerIndex}`;
    setAcceptingOffer(offerKey);
    setIsLoading(true);
    setErrorMessage("");
    setInfoMessage("");
    try {
      const signer = await getMetaMaskSigner();
      const contract = getContract("ecofutures-token", signer);
      if (contract) {
        const result = await contract.withdrawOffer(tokenId, offerIndex); //tokenID & offerIndex
        console.log({ result });
        // Use the checkTransactionConfirmationReceipt without passing library
        const r = await checkTransactionConfirmationReceipt(result.hash);
        console.log({ r });
        const receipt = r as any;
        if (receipt?.status !== "0") {
          console.log("NFT offer accepted");
          setInfoMessage("NFT offer accepted");
          window.location.reload();
        }
      }
    } catch (error: any) {
      console.log({ error });
      // Handle MetaMask rejection
      if (error.code === 4001 || error.message?.includes("User rejected") || error.code === "ACTION_REJECTED") {
        setErrorMessage("Transaction was rejected by user");
        return;
      }
      setErrorMessage("Failed to accept offer");
    } finally {
      setIsLoading(false);
      setAcceptingOffer(null);
    }
  };

  const easements = token.easements?.length ? token.easements.sort((a, b) => a.level - b.level) : undefined;

  // const offerLandNameCol: TableSchema<any> = {
  //   id: "name",
  //   label: "Name",
  //   sx: { width: 150, maxWidth: 150, minWidth: 150 },
  //   headerStyle: { width: 150, maxWidth: 150, minWidth: 150 },
  //   render: (data) => {
  //     return (
  //       <Link
  //         style={{ textDecoration: "none" }}
  //         to={generatePath(AppRoutes.Token.path, { tokenId: data?.nftId || "" })}
  //       >
  //         <Text
  //           numeric
  //           contrast
  //           capital
  //           sx={{
  //             fontSize: { xs: 14, md: 16 },
  //             textDecoration: "underline",
  //             whiteSpace: "wrap",
  //             lineHeight: 1.2,
  //           }}
  //         >
  //           {data?.name}
  //         </Text>
  //         <Text numeric sx={{ color: "text.disabled", lineHeight: 1.2 }}>
  //           #{data?.nftId}
  //         </Text>
  //       </Link>
  //     );
  //   },
  // };

  // const offerThumbCol: TableSchema<any> = {
  //   id: "content",
  //   label: "Land",
  //   sx: { width: sizing(55) },
  //   headerStyle: { width: sizing(55) },
  //   skeleton: () => {
  //     return <Skeleton sx={{ width: sizing(50), height: sizing(50) }} />;
  //   },
  //   render: (data) => {
  //     return (
  //       <Link
  //         sx={{ width: sizing(50), height: sizing(50), margin: "0" }}
  //         to={generatePath(AppRoutes.Token.path, { tokenId: data?.nftId || "" })}
  //       >
  //         <Img alt="Thumb" sx={{ width: "100%", objectFit: "cover", height: "100%" }} src={data?.image} />
  //       </Link>
  //     );
  //   },
  // };

  const acceptOfferActionSchema: TableSchema<any> = {
    id: "accept",
    label: "Action",
    render: (data) => {
      const uniqueId = `accept-offer-btn-${data?.nftId}-${data?.offerIndex}`;
      const offerKey = `${data?.nftId}-${data?.offerIndex}`;
      return (
        <Row sx={{ gap: 3 }}>
          <Button
            id={uniqueId}
            size="small"
            variant="contained"
            sx={{ minWidth: "unset" }}
            onClick={() =>
              handleAcceptOffer(Number(data?.nftId), Number(data?.offerIndex), data?.buyer, Number(data?.amount))
            }
            disabled={isLoading && acceptingOffer !== offerKey}
          >
            {isLoading && acceptingOffer === offerKey ? `Accepting...` : `Accept`}
          </Button>
        </Row>
      );
    },
  };

  const withdrawOfferActionSchema: TableSchema<any> = {
    id: "withdraw",
    label: "Action",
    render: (data) => {
      console.log({ data });
      const uniqueId = `accept-offer-btn-${data?.nftId}-${data?.offerIndex}`;
      const offerKey = `${data?.nftId}-${data?.offerIndex}`;
      const canWithdraw = data?.buyer === user?.address;
      return (
        <>
          {canWithdraw ? (
            <Row sx={{ gap: 3 }}>
              <Button
                id={uniqueId}
                size="small"
                variant="contained"
                sx={{ minWidth: "unset" }}
                onClick={() => handleWithdrawOffer(Number(data?.nftId), Number(data?.offerIndex))}
                disabled={isLoading && acceptingOffer !== offerKey}
              >
                {isLoading && acceptingOffer === offerKey ? `Withdrawing...` : `Withdraw`}
              </Button>
            </Row>
          ) : (
            <Row sx={{ gap: 3 }} />
          )}
        </>
      );
    },
  };

  // const actionOfferSchema: TableSchema<LandToken> = {
  //   id: "actions",
  //   label: "Action",
  //   render: () => {
  //     return (
  //       <>
  //         <Text numeric contrast medium capital>
  //           Offered
  //         </Text>
  //       </>
  //     );
  //   },
  // };

  const amountOfferSchema: TableSchema<any> = {
    id: "offer",
    label: "Offer",
    render: (data) => {
      return (
        <>
          <Text numeric contrast medium capital>
            {data?.amount ? toReadable(data.amount, false, true) : "N/A"} <CurrencyLabel currency={"USD"} />
          </Text>
        </>
      );
    },
  };
  const fromOfferSchema: TableSchema<any> = {
    id: "from",
    label: "From",
    render: (data) => {
      return <AddressLink address={data.buyer as string} type={"user"} />;
    },
  };
  // const toOfferSchema: TableSchema<any> = {
  //   id: "to",
  //   label: "To",
  //   render: () => {
  //     return (
  //       <AddressLink address={user?.address as string} type={"user"} />
  //     );
  //   },
  // };

  // const floorOfferSchema: TableSchema<any> = {
  //   id: "floor",
  //   label: "Price Difference %", // Changed label to indicate percentage
  //   render: (data) => {
  //     // Ensure remainingTrees is treated as a string before replacing commas
  //     const cleanedRemainingTreesStr = String(remainingTrees).replace(/,/g, '');
  //     const remainingTreesNum = Number(cleanedRemainingTreesStr);
  //     const offerAmountNum = Number(data?.amount);

  //     let percentageText = "N/A";

  //     // Check if both numbers are valid
  //     if (data?.amount && !isNaN(remainingTreesNum) && !isNaN(offerAmountNum)) {
  //       const difference = offerAmountNum - remainingTreesNum;
  //       console.log({ difference, data, remainingTrees: cleanedRemainingTreesStr, remainingTreesNum, offerAmountNum }); // Keep existing log

  //       if (remainingTreesNum > 0) {
  //         const percentageDifference = (difference / remainingTreesNum) * 100;
  //         // Check for finite number before formatting
  //         if (isFinite(percentageDifference)) {
  //           percentageText = `${percentageDifference.toFixed(2)}%`; // Format as percentage with 2 decimal places
  //         } else {
  //           percentageText = "Error"; // Handle potential division issues resulting in Infinity
  //         }
  //       } else if (remainingTreesNum === 0) {
  //         // Handle division by zero case
  //         if (offerAmountNum > 0) {
  //           percentageText = "Infinite %"; // Offer is infinitely higher than zero floor
  //         } else {
  //           percentageText = "0.00%"; // Both offer and floor are zero
  //         }
  //       }
  //     }

  //     return (
  //       <Text numeric contrast medium capital>
  //         {percentageText}
  //         {/* {remainingTrees} */} {/* Removed original display */}
  //       </Text>
  //     );
  //   },
  // };

  const acceptOfferSchema: TableSchema<any>[] = [
    // offerThumbCol,
    // offerLandNameCol,
    // actionOfferSchema,
    amountOfferSchema,
    fromOfferSchema,
    // toOfferSchema,
    //  floorOfferSchema,
    timeCol,
    acceptOfferActionSchema,
  ];

  const offerSchema = [
    // offerThumbCol,
    // offerLandNameCol,
    // actionOfferSchema,
    amountOfferSchema,
    fromOfferSchema,
    // toOfferSchema,
    //floorOfferSchema,
    timeCol,
    withdrawOfferActionSchema,
  ];

  const getTransactions = () => {
    const provenanceSchema = isMobile ? ProvenanceMobileSchema : ProvenanceSchema;
    const bidSchema = BidSchema;
    const OfferSchema = offerSchema;
    return (
      <>
        {onlyOffers ? (
          <TableWithTabs sx={{ backgroundColor: "back.table", borderRadius: "20px" }}>
            <TabsRow
              sx={{
                p: sizing(6),
                pb: sizing(0),
                borderRadius: "20px 20px 0px 0px",
                marginBottom: 0,
                display: user?.trusteeStatus === "accepted" ? "none" : "block",
                visibility: user?.trusteeStatus === "accepted" ? "hidden" : "visible",
              }}
            >
              <FilterBlock
                sx={{
                  "@media (min-width: 600px)": {
                    display: "flex",
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                  },
                  "@media (max-width: 599px)": {
                    flexDirection: "row",
                    justifyContent: "start",
                  },
                }}
              >
                <ActionBtn variant="contained" size="medium" disabled>
                  Offers
                </ActionBtn>
              </FilterBlock>
            </TabsRow>
            <div style={{ width: "100%" }}>
              <TableComponent
                mobileTitle="Offers"
                hideTitle
                schema={isOwner ? acceptOfferSchema : OfferSchema}
                apiURL={""}
                batch={5}
                external={false}
                equalColumnWidths={true}
                title={"Offers"}
                totalItems={offersData?.length || offerList.length}
                customData={offersData}
                errorName="Offers"
              />
            </div>
          </TableWithTabs>
        ) : (
          <TableWithTabs
            ref={provenanceRef}
            id="provenance"
            sx={{ backgroundColor: "back.table", borderRadius: "20px" }}
          >
            <TabsRow
              sx={{
                p: sizing(6),
                pb: sizing(6),
                borderRadius: "20px 20px 0px 0px",
                marginBottom: 0,
                display: user?.trusteeStatus === "accepted" ? "none" : "block",
                visibility: user?.trusteeStatus === "accepted" ? "hidden" : "visible",
              }}
            >
              <FilterBlock
                sx={{
                  "@media (min-width: 600px)": {
                    display: "flex",
                    overflowX: "auto",
                    whiteSpace: "nowrap",
                  },
                  "@media (max-width: 599px)": {
                    flexDirection: "row",
                    justifyContent: "start",
                  },
                }}
              >
                <ActionBtn
                  className={activeTab === "provenance" ? "active" : ""}
                  disabled={activeTab === "provenance"}
                  variant="outlined"
                  size="medium"
                  onClick={() => setActiveTab("provenance")}
                  style={{ textTransform: "uppercase" }}
                  sx={{
                    fontSize: "14px",
                    fontFamily: "monospace",
                    textTransform: "uppercase",
                    fontWeight: "700",
                  }}
                >
                  Provenance
                </ActionBtn>
                {token.offerStatus === "opened" && offerList.length > 0 && (
                  <ActionBtn
                    className={activeTab === "offers" ? "active" : ""}
                    disabled={activeTab === "offers"}
                    variant="outlined"
                    size="medium"
                    onClick={() => setActiveTab("offers")}
                    style={{
                      textTransform: "uppercase",
                    }}
                    sx={{
                      fontSize: "14px",
                      fontFamily: "monospace",
                      textTransform: "uppercase",
                      fontWeight: "700",
                    }}
                  >
                    Offers
                  </ActionBtn>
                )}
                {token?.etNft?.etAuction?.status === "started" && currentBids.length > 0 && (
                  <ActionBtn
                    className={activeTab === "bids" ? "active" : ""}
                    disabled={activeTab === "bids"}
                    variant="outlined"
                    size="medium"
                    style={{ textTransform: "uppercase" }}
                    onClick={() => setActiveTab("bids")}
                    sx={{
                      fontSize: "14px",
                      fontFamily: "monospace",
                      textTransform: "uppercase",
                      fontWeight: "700",
                    }}
                  >
                    Bids
                  </ActionBtn>
                )}
              </FilterBlock>
            </TabsRow>

            {activeTab === "provenance" && (
              <div style={{ width: "100%" }}>
                <TableComponent
                  mobileTitle="Provenance"
                  hideTitle
                  schema={provenanceSchema}
                  apiURL={""}
                  batch={5}
                  external={false}
                  title={"Provenance"}
                  totalItems={transactionsCount}
                  customData={transactionsData}
                />
              </div>
            )}
            {activeTab === "bids" && (
              <div style={{ width: "100%" }}>
                <TableComponent
                  mobileTitle="Bids"
                  hideTitle
                  totalItems={currentBids.length}
                  customData={currentBids}
                  schema={bidSchema}
                  apiURL={""}
                  batch={5}
                  title={"Bids"}
                  external={false}
                />
              </div>
            )}

            {activeTab === "offers" && (
              <div style={{ width: "100%" }}>
                {/* <TableComponent
                  mobileTitle="Offers"
                  hideTitle
                  schema={isOwner ? acceptOfferSchema : OfferSchema}
                  apiURL={apiRoutes.token.offers(token?.id as string)}
                  batch={5}
                  external
                  title={"Offers"}
                  totalItems={offerList.length}
                  customData={[]}
                /> */}
                <TableComponent
                  mobileTitle="Offers"
                  hideTitle
                  schema={isOwner ? acceptOfferSchema : OfferSchema}
                  apiURL={""}
                  batch={5}
                  external={false}
                  equalColumnWidths={true}
                  title={"Offers"}
                  totalItems={offersData?.length || offerList.length}
                  customData={offersData}
                  errorName="Offers"
                />
              </div>
            )}
          </TableWithTabs>
        )}
      </>
    );
  };

  const isBasicPlanActive = () => {
    if (token.etNft?.etAuction.winner) {
      return true;
    } else {
      return !!token.etNft?.easementActivated?.active;
    }
  };
  if (!compact) {
    return (
      <>
        {onlyOffers ? (
          <Provenance sx={{ backgroundColor: "transparent" }}>{getTransactions()}</Provenance>
        ) : onlyEasements ? (
          <Grid container p={0} sx={{ mt: "auto", paddingTop: sizing(0), backgroundColor: "#ccc" }}>
            <EasementsWrap sx={{ paddingX: sizing(6), pb: sizing(6), borderRadius: "20px" }}>
              <Attention inverted sx={{ border: "none" }}>
                <Text medium>Land easements</Text>
              </Attention>
              <BasicPlan>
                <div>
                  <Text medium sx={{ color: "#3d3d3d", alignItems: "center", mb: 6 }}>
                    {isBasicPlanActive() && <PulseCircle />}
                    Tier 1 {isBasicPlanActive() ? "(Active)" : ""}
                  </Text>

                  <Text large numeric sx={{ gap: 3 }}>
                    {toReadable(token.meta?.startingBid)} <Text large>{CURRENCY_NAME}</Text>
                  </Text>
                </div>
                <div>
                  <div>
                    <CollapseBlock title="Plan" content={token?.meta?.bfd_plan} benefits={token?.meta?.basicPerks} />
                  </div>
                </div>
              </BasicPlan>
              {easements && (
                <Easements>
                  {easements.map((item, index) => {
                    const easementActive = token.etNft?.easementActivated?.active;
                    let level = 0;
                    if (easementActive) {
                      level = token.etNft?.easementActivated?.level || 0;
                    } else if (token.etNft?.etAuction.endingPrice && token.etNft?.etAuction.winner != null) {
                      const val = formatValue(token.etNft?.etAuction.endingPrice, false);
                      const itemVal = formatValue(item.activationBid, false);
                      if (itemVal <= val) {
                        level = index + 1;
                      }
                    }

                    return (
                      <Easement
                        hideBidding={true}
                        key={index}
                        activated={level > index}
                        number={index + 1}
                        value={item}
                      />
                    );
                  })}
                </Easements>
              )}
            </EasementsWrap>
          </Grid>
        ) : onlyProvenance ? (
          <Provenance sx={{ backgroundColor: "transparent" }}>{getTransactions()}</Provenance>
        ) : (
          <Grid container p={0} sx={{ mt: "auto", paddingTop: sizing(0), backgroundColor: "#ccc" }}>
            <EasementsWrap sx={{ paddingX: sizing(6), pb: sizing(6), borderRadius: "20px" }}>
              <Attention inverted sx={{ border: "none" }}>
                <Text medium>Land easements</Text>
              </Attention>
              <BasicPlan>
                <div>
                  <Text medium sx={{ color: "#3d3d3d", alignItems: "center", mb: 6 }}>
                    {isBasicPlanActive() && <PulseCircle />}
                    Tier 1 {isBasicPlanActive() ? "(Active)" : ""}
                  </Text>

                  <Text large numeric sx={{ gap: 3 }}>
                    {toReadable(token.meta?.startingBid)} <Text large>{CURRENCY_NAME}</Text>
                  </Text>
                </div>
                <div>
                  <div>
                    <CollapseBlock title="Plan" content={token?.meta?.bfd_plan} benefits={token?.meta?.basicPerks} />
                  </div>
                </div>
              </BasicPlan>
              {easements && (
                <Easements>
                  {easements.map((item, index) => {
                    const easementActive = token.etNft?.easementActivated?.active;
                    let level = 0;
                    if (easementActive) {
                      level = token.etNft?.easementActivated?.level || 0;
                    } else if (token.etNft?.etAuction.endingPrice && token.etNft?.etAuction.winner != null) {
                      const val = formatValue(token.etNft?.etAuction.endingPrice, false);
                      const itemVal = formatValue(item.activationBid, false);
                      if (itemVal <= val) {
                        level = index + 1;
                      }
                    }

                    return (
                      <Easement
                        hideBidding={true}
                        key={index}
                        activated={level > index}
                        number={index + 1}
                        value={item}
                      />
                    );
                  })}
                </Easements>
              )}
            </EasementsWrap>

            <Provenance sx={{ backgroundColor: "transparent" }}>{getTransactions()}</Provenance>
          </Grid>
        )}
      </>
    );
  }

  // const shouldShowTiers = () => {
  //   if (token.etNft?.easementActivated?.contractStart) {
  //     const end = moment(token.etNft.easementActivated.contractStart).add(token.meta?.contractPeriod, "days");
  //     return end.isAfter();
  //     //|| !!end;
  //   } else {
  //     return false;
  //   }
  // };

  // if (auctionStatus === "cancelled" || (auctionStatus === "ended" && !winnerAddress)) {
  //   return null;
  // }

  // if ((auctionStatus === "completed" && !winnerAddress && user) ) {
  //   handleCancelAuction();
  // }

  const handleCloseModal = () => {
    setBiddingModal(false);
    setBidState("initial");
  };

  // const handlePlaceBid = () => {
  //   setBidState('processing');

  //   // Simulate bid processing
  //   setTimeout(() => {
  //     alert("Bid placed successfully!");
  //     handleCloseModal();
  //   }, 3000);
  // };

  const handleCancelBid = () => {
    setBidState("initial");
  };

  const renderPopupContent = () => {
    switch (bidState) {
      case "initial":
        return (
          <>
            {/* Stats Section */}
            <StatsGrid>
              <StatItem>
                <StatLabel>{bidData.tier}</StatLabel>
                <StatValue>{bidData.tierPrice}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Mint capacity</StatLabel>
                <StatValue>{bidData.mintCapacity}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Contract period</StatLabel>
                <StatValue>{bidData.contractPeriod}</StatValue>
              </StatItem>
              <Divider />
              <StatItem>
                <StatLabel>Current bid</StatLabel>
                <StatValue>{bidData.currentBid}</StatValue>
              </StatItem>
            </StatsGrid>

            {/* Bid Input Section */}
            <BidInputContainer>
              <BidInputField
                value={nextBid}
                onChange={(e) => validateNextBid(e.target.value)}
                variant="outlined"
                fullWidth
                style={{ color: "black" }}
                type="number"
                inputMode="decimal"
                placeholder="0.00"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ "& p": { color: "black" } }}>
                      USD
                    </InputAdornment>
                  ),
                }}
              />
            </BidInputContainer>

            {/* Action Button */}
            <ActionButtonNew
              onClick={submitBid}
              disabled={isDisabled || isExpired || txnLoading || !canBid || !nextBidValid}
              variant="contained"
              fullWidth
              style={{ width: "100%" }}
            >
              Place bid
            </ActionButtonNew>

            {/* Available Balance */}
            <BalanceText>Available balance {bidData.availableBalance}</BalanceText>
          </>
        );

      case "processing":
        return (
          <>
            {/* Stats Section */}
            <StatsGrid>
              <StatItem>
                <StatLabel>{bidData.tier}</StatLabel>
                <StatValue>{bidData.tierPrice}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Mint capacity</StatLabel>
                <StatValue>{bidData.mintCapacity}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Contract period</StatLabel>
                <StatValue>{bidData.contractPeriod}</StatValue>
              </StatItem>
              <Divider />
              <StatItem>
                <StatLabel>Current bid</StatLabel>
                <StatValue>{bidData.currentBid}</StatValue>
              </StatItem>
            </StatsGrid>

            {/* Bid Input Section */}
            <BidInputContainer>
              <BidInputField
                value={nextBid}
                onChange={(e) => validateNextBid(e.target.value)}
                variant="outlined"
                fullWidth
                type="number"
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ "& p": { color: "black" } }}>
                      USD3
                    </InputAdornment>
                  ),
                }}
              />
            </BidInputContainer>

            {/* Cancel Button */}
            <CancelButton onClick={handleCancelBid} variant="contained" fullWidth style={{ width: "100%" }}>
              Cancel
            </CancelButton>

            {/* Processing Message */}
            <ProcessingText>{infoMessage || errorMessage || "Processing your bid..."}</ProcessingText>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Grid container p={0} sx={{ mt: "auto", paddingTop: sizing(0) }}>
      <Grid item xs={12} className={hasAuction ? (token.etNft ? "" : "hidden") : "hidden"}>
        <Grid container spacing={2}>
          <Grid
            item
            xs={6}
            sm={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              direction: "ltr",
            }}
          >
            <Text
              sx={{
                fontSize: isSmallScreen ? "12px" : "12px",
                fontWeight: 700,
                color: "#999",
                fontFamily: "monospace",
                lineHeight: "18px",
              }}
            >
              {
                // token.offerStatus === "opened" && hasBids
                //   ? "Highest Offer" :
                token.etNft?.status === "sold" ||
                  token.etNft?.etAuction.status === "completed" ||
                  token.etNft?.etAuction.status === "ended" ||
                  token.etNft?.etAuction.status === "cancelled"
                  ? token.etNft.etAuction.winner || token.etNft.ownerAddress !== token.etNft.minterAddress
                    ? // V9.1: the sale is only final once settlement reaches "finalised" —
                      // until then the winner holds no EFT and rewards aren't assigned.
                      token.etNft?.settlementState &&
                      token.etNft.settlementState !== "finalised" &&
                      token.etNft.settlementState !== "none"
                      ? "Settling at"
                      : "Sold for"
                    : "Not sold"
                  : hasBids
                    ? "Current bid"
                    : "Opening bid"
              }
            </Text>
            <Text numeric bold contrast sx={{ fontSize: "14px", fontWeight: 700, lineHeight: "18px" }}>
              ${formatValue(currentBid, true, 2)}
              {/* {(token.offerStatus === "opened" && hasBids)
                ? formatValue(highestOffer || 0, true, 2)
                :
                formatValue(currentBid, true, 2)} */}
            </Text>
            {highestBidderAddress && !winnerAddress && (
              <div style={{ display: "flex", gap: "10px", lineHeight: "18px" }}>
                <Text numeric bold contrast sx={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase" }}>
                  by
                </Text>
                <ClickableContainer
                  sx={{
                    padding: "0px 8px",
                    margin: "0px -8px",
                  }}
                >
                  <AddressLink
                    type="user"
                    address={highestBidderAddress}
                    sx={{
                      width: "100%",
                      "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                      "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
                      "& a": {
                        color: "#999 !important",
                        fontWeight: "bold",
                        fontSize: { xs: "12px", sm: "12px" },
                      },
                    }}
                  />
                </ClickableContainer>
              </div>
            )}
            {winnerAddress && (
              <div style={{ display: "flex", gap: "10px" }}>
                <span>
                  {" "}
                  <ArrowForwardSharpIcon sx={{ fontSize: "12px", color: "#999", fontWeight: "700" }} />
                </span>
                <ClickableContainer
                  sx={{
                    padding: "0px 8px",
                    margin: "0px -8px",
                  }}
                >
                  <AddressLink
                    type="user"
                    address={winnerAddress}
                    sx={{
                      width: "100%",
                      "& a.userAddress, & a.trustAddress": { textDecoration: "none" },
                      "& a.userAddress:hover, & a.trustAddress:hover": { textDecoration: "none" },
                      "& a": {
                        color: "#999 !important",
                        fontWeight: "bold",
                        fontSize: { xs: "12px", sm: "12px" },
                      },
                    }}
                  />
                </ClickableContainer>
              </div>
            )}
            {!winnerAddress && (
              <div style={{ display: "flex", gap: "10px" }}>
                <AuctionTime
                  sx={{
                    lineHeight: "18px",
                    fontWeight: "700",
                    direction: "ltr",
                    fontSize: "12px",
                    color: "#999",
                    fontFamily: "monospace",
                    textTransform: "uppercase",
                  }}
                >
                  {auctionEndTs}
                </AuctionTime>
              </div>
            )}
          </Grid>
          <Grid
            item
            xs={6}
            sm={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              direction: { xs: "ltr", sm: "rtl" },
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", sm: "55%" },
                justifyItems: { xs: "start", sm: "end" },
                pb: 6,
              }}
            >
              {mintPerDay ? (
                <>
                  <Text
                    sx={{
                      fontSize: isSmallScreen ? "14px" : "14px",
                      fontWeight: 400,
                      color: "#999",
                      mt: sizing(3),
                    }}
                  >
                    Mint/day
                  </Text>

                  <Text numeric sx={{ mt: 1.5, flexDirection: "column", fontSize: "16px", fontWeight: 700 }}>
                    {getMintRate()} <CurrencyLabel sx={{ fontSize: "16px", fontWeight: 700 }} currency="TREE" />
                  </Text>
                </>
              ) : (
                <>
                  <Text
                    sx={{
                      fontSize: isSmallScreen ? "14px" : "12px",
                      fontWeight: 400,
                      color: "#999",
                      direction: "ltr",
                      mt: sizing(3),
                    }}
                  >
                    {winnerAddress ? "Value" : auctionEnded ? `Value` : `Auction ends in`}
                  </Text>
                  {winnerAddress && (
                    <Text medium numeric bold contrast sx={{ mt: 1.5, flexDirection: "column", direction: "ltr" }}>
                      <Text sx={{ fontSize: "16px", fontWeight: 700 }}>
                        {sixDigits(remainingTrees)}
                        <CurrencyLabel sx={{ fontSize: "16px", fontWeight: 700 }} currency="USD" />
                      </Text>
                      <Text sx={{ fontSize: "14px", fontWeight: 700, color: "#999" }}>{treesValue}</Text>
                    </Text>
                  )}
                  {!auctionEnded && (
                    <CountdownTimer
                      medium
                      sx={{ mt: 1.5, fontWeight: "700", direction: "ltr", "& .css-73efjq": { fontSize: "16px" } }}
                      targetDate={endDate}
                    />
                  )}
                  {auctionEnded && !winnerAddress && (
                    <Text medium numeric bold contrast sx={{ mt: 1.5, flexDirection: "column", direction: "ltr" }}>
                      <Text sx={{ fontSize: "16px", fontWeight: 700 }}>
                        {sixDigits(remainingTrees)}
                        <CurrencyLabel sx={{ fontSize: "16px", fontWeight: 700 }} currency="USD" />
                      </Text>
                      <Text sx={{ fontSize: "14px", fontWeight: 700, color: "#999" }}>{treesValue}</Text>
                    </Text>
                  )}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
        {/* <Grid item xs={12} sx={{
          display: 'flex',
          flexDirection: 'column',
          direction: 'ltr',
          margin: '16px 0px 20px 0px',
        }}>
          {!auctionEnded && canBid && !isExpired && (
            <ActionButton
              disabled={biddingModal || isDisabled || isExpired || endingInProcess}
              onClick={() => {
                if (user?.emailVerified && library) {
                  handleBid();
                } else if (!user) {
                  window.dispatchEvent(new Event('openWalletDialog'));
                } else {
                  navigator(AppRoutes.Profile.subRoutes.Identification.path);
                }

              }}
              style={{ width: '100%' }}
              sx={{
                margin: 0,
                width: '100%',
                '&:hover': {
                  border: "none"
                },
                // To style the disabled state, use the MUI sx pseudo-class selector
                '&.Mui-disabled': {
                  backgroundColor: 'black',
                  color: 'white',
                  border: "none",
                  cursor: 'not-allowed',
                  '&hover': {
                    border: "none"
                  }
                },
              }}
            >
              {(user?.emailVerified && library) ? "Place bid" : user ? "Verify" : "Connect Wallet"}
            </ActionButton>
          )}
          {!isExpired && !auctionLoading && !auctionEnded && !canBid && !isTrust && (
            <ActionButton
              disabled={confirmModal || txnLoading || isDisabled}
              color="primary"
              variant="contained"
              onClick={() => handleCancelButton()}
              style={{
                width: '100%',
              }}
            >
              Cancel Auction
            </ActionButton>
          )}
          {(endingInProcess || auctionLoading) && (
            <AuctionLoading>
              <CircularProgress />
            </AuctionLoading>
          )}
        </Grid> */}
      </Grid>

      {/* <DialogInfo
        open={approveOpen}
        onClose={() => setApproveOpen(false)} title={""}>

        <Wrapper
          sx={{
            p: 12,
            backgroundColor: "white",
            borderColor: 'white',
          }}
        >
          <IconContainer>
            <CircularIcon>
              <InfoIconOutlined fontSize="large" color="primary" />
            </CircularIcon>
          </IconContainer>
          <InfoBody>
            <br />
            <p style={{ fontWeight: 600, color: 'black', alignSelf: "center", fontSize: "26px" }}>Are you sure</p>
            <br />
            <p style={{ lineHeight: "1.5", textAlign: "center" }}>
              Accept this offer?


            </p>
          </InfoBody>
          <Grid container direction={'row'} alignItems="center" justifyContent={"space-around"}
            sx={{
              borderBottom: "1px solid GrayText",
              pb: 8,
              width: "100%",
            }}
          >

            <InfoButton onClick={() => setApproveOpen(false)} style={{ borderColor: "#999999", backgroundColor: "#999999" }}>
              Cancel
            </InfoButton>
            <InfoButton onClick={() => handleAcceptOffer(Number(offerRowData?.nftId), Number(offerRowData?.offerIndex))}>
              {isLoading ? "Loading" : "Accept offer"}
            </InfoButton>
          </Grid>
          <br />
         

        </Wrapper >

      </DialogInfo> */}

      {/* <DialogPopUp
      {!isSmallScreen ? (
        < DialogPopUp
          open={biddingModal}
          onClose={() => {
            if (!txnLoading) {
              handleCloseBidding();
            }
          }}
        >

          <DialogContent sx={{ p: sizing(0), backgroundColor: "transparent", maxWidth: '600px' }}>

            <Grid
              container
              direction={'row'}
              alignItems="center"
              height={isSmallScreen ? '100%' : '300px'}
            >
              <Grid
                xs={12}
                md={6}
                width={'300px'}
                order={isSmallScreen ? 0 : 1} // Image at top for mobile, right for desktop
                sx={{
                  padding: "20px",
                  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${token.content})`,
                  backgroundColor: "white",
                  backgroundSize: 'cover', // Optional: to cover the entire area
                  backgroundPosition: 'center', // Optional: to center the image
                  height: isSmallScreen ? mobileImageHeight : '300px',
                  maxHeight: isSmallScreen ? mobileImageHeight : '300px',
                  width: '300px',
                  borderRadius: isSmallScreen ? "16px 16px 0 0" : "0 16px 16px 0",
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    height: isSmallScreen ? '100%' : '100%',
                  }}>

                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography
                      variant="subtitle2"
                      fontFamily={"monospace"}
                      fontWeight={600}
                      color="white"
                      fontSize={"14px"}
                    >
                      {token.meta?.name}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      fontFamily={"monospace"}
                      fontWeight={600}
                      color="white"
                      fontSize={"14px"}
                    >
                      #{token.etNft?.id}
                    </Typography>
                  </div>

                  <Grid container spacing={2} sx={{ marginTop: '16px' }}>
                    {biddingTiers?.map((tier, index) => (
                      <React.Fragment key={index}>
                        <Grid item xs={6}>
                          <Typography
                            variant="subtitle2"
                            fontFamily={"monospace"}
                            fontWeight={400}
                            color="white"
                            fontSize={"14px"}
                          >
                            <strong> {tier.title}</strong>
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="subtitle2"
                            fontFamily={"monospace"}
                            fontWeight={400}
                            color="white"
                            align="right"
                            fontSize={"14px"}
                          >
                            {formatValue(tier.price)} {CURRENCY_NAME}
                          </Typography>
                        </Grid>
                      </React.Fragment>
                    ))}
                  </Grid>
                  <Box sx={{ flexGrow: 1 }} />

                  {canBid && !isSmallScreen && (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        disabled={isDisabled || isExpired || txnLoading || !canBid || !nextBidValid}
                        variant="outlined"
                        onClick={() => submitBid()}
                        sx={{ color: 'white', border: '1px solid white', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}

                      >
                        {txnLoading ? <CircularProgress size={24} /> : "Submit bid"}
                      </Button>
                    </div>
                  )}

                </Box>
              </Grid>
              <Grid
                width={'300px'}
                xs={12}
                md={6}
                style={{
                  backgroundColor: 'white',
                  width: '300px',
                  height: isSmallScreen ? 'inherit' : '300px',
                  maxHeight: isSmallScreen ? '300px' : '300px',
                }}
                order={isSmallScreen ? 1 : 0}
                sx={{
                  borderRadius: isSmallScreen ? "0 0 16px 16px" : "16px 0 0 16px",
                }}
              >
                <Box
                  sx={{
                    height: `${isSmallScreen ? '300px' : '300px'}`,
                    maxHeight: isSmallScreen ? '300px' : '300px',
                    padding: "20px",
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <div>
                    <DialogTitle sx={{ padding: '0px', fontWeight: "600", fontSize: "14px" }} align="left">Place a bid</DialogTitle>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-evenly',
                        height: isSmallScreen ? '200px' : '220px',
                      }}>
                      <Grid container spacing={4} fontSize={"14px"}>
                        {BidValues.map((_, rowIndex) => (
                          <React.Fragment key={rowIndex}>
                            <Grid item xs={6} color={"GrayText"} >
                              {Object.keys(BidValues[rowIndex])[0]}
                            </Grid>
                            <Grid item xs={6} style={{ display: "flex", justifyContent: "end" }}>
                              {Object.values(BidValues[rowIndex])[0]}
                            </Grid>
                          </React.Fragment>
                        ))}
                      </Grid>

                      <AuctionInput>
                        <TextField
                          type="number"
                          disabled={!canBid || isExpired || isDisabled}
                          error={!!errorMessage}
                          placeholder="0.0000"
                          onChange={(e) => validateNextBid(e.target.value)}
                          value={nextBid}
                        />
                        <span>{CURRENCY_NAME}</span>
                      </AuctionInput>
                      {canBid && isSmallScreen && (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          <Button
                            disabled={isDisabled || isExpired || txnLoading || !canBid || !nextBidValid}
                            variant="outlined"
                            style={{ width: "100%", color: 'white', }}
                            onClick={() => submitBid()}
                            sx={{ color: 'white', border: '1px solid black', backgroundColor: 'black' }}
                          >
                            {txnLoading ? <CircularProgress size={24} /> : "Submit bid"}
                          </Button>
                        </div>
                      )}
                      {errorMessage && (
                        <Text medium sx={{ color: "error.main", mb: 6 }}>
                          {errorMessage}
                        </Text>
                      )}
                      {infoMessage &&
                        <Text medium sx={{ color: "primary", mb: 6 }}>
                          {infoMessage}
                        </Text>
                      }

                    </div>
                  </div>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>



        </DialogPopUp >
      ) : ( */}
      <BidDialog open={biddingModal} onClose={handleCloseModal} maxWidth={false}>
        <BidPopupContainer isMobile={isMobile}>
          {/* Top Image Section */}
          <ImageSection
            isMobile={isMobile}
            style={{
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${token.content})`,
            }}
          >
            <EFTTitle>{token.meta?.name}</EFTTitle>
            <EFTId>#{token.etNft?.id}</EFTId>
          </ImageSection>

          {/* Bottom White Section */}
          <ContentSection isMobile={isMobile}>{renderPopupContent()}</ContentSection>
        </BidPopupContainer>
      </BidDialog>

      {/* Cancel-auction confirmation, on the shared land-card + purple-panel shell so it
          matches the live copy in EFTActionButton. Nothing in this file opens it any more
          (`setConfirmModal(true)` has no caller — the Cancel Auction button below fires
          handleCancelAuction directly), but it is kept on the shell rather than the old
          frosted panel so the two do not drift apart if it is wired back up. */}
      <LandActionPopup
        open={confirmModal}
        onClose={() => {
          if (!txnLoading) {
            handleCloseConfirm();
          }
        }}
        token={token}
        statLabel={hasBids ? "CURRENT BID" : "OPENING BID"}
        statValue={`$${formatValue(currentBid)}`}
        statAddress={highestBidderAddress || null}
      >
        {!isExpired && (
          <PopupCTA disabled={txnLoading} onClick={() => handleCancelAuction()} variant="contained">
            {txnLoading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Cancel auction"}
          </PopupCTA>
        )}
      </LandActionPopup>
    </Grid>
  );
};

export const InfoMessage = styled("span")`
  font-size: 12px;
  margin-top: 10px;
`;

const AuctionTime = styled("div")`
  font-size: 16px;
  font-family: IBM Mono, monospace;
  font-weight: 600;
`;

const Provenance = styled("div")`
  width: 100%;
`;

export const AuctionCard = styled("div")`
  width: 100%;
  padding: 24px;
  border-radius: 16px;
  border: var(--border);
  height: max-content;
  margin-top: 24px;
  position: relative;

  &.hidden {
    display: none;
  }

  @media (max-width: 992px) {
    margin-top: 12px;
    padding: 12px;
  }
`;

type CollapseBlockProps = {
  title: string;
  content: any;
  benefits?: any;
};
const CollapseBlock = ({ title, content, benefits }: CollapseBlockProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [expanded, setExpanded] = React.useState(true);

  // // Update expansion state when screen size changes
  // useEffect(() => {
  //   setExpanded(!isSmallScreen);
  // }, [isSmallScreen]);
  return (
    <Grid width="100%" flexDirection="column" sx={{ background: "#dbdbdb" }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          boxShadow: "none",
          borderRadius: 0,
        }}
      >
        <AccordionSummary
          expandIcon={isSmallScreen && title !== "Benefits" ? <ExpandMoreIcon /> : null}
          aria-controls="panel1d-content"
          id="panel1d-header11"
          sx={{ paddingY: sizing(1), background: "#dbdbdb" }}
        >
          <Text sx={{ fontSize: "16px", color: "#6a6a6a" }} medium>
            {title}
          </Text>
        </AccordionSummary>
        <AccordionDetails sx={{ background: "#dbdbdb", paddingBottom: "24px" }}>
          <Text medium sx={{ display: "block" }}>
            {content}
          </Text>
        </AccordionDetails>
      </Accordion>
      {benefits?.length ? (
        <>
          <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{
              boxShadow: "none",
              borderRadius: 0,
              "&::before": {
                display: "none",
              },
            }}
          >
            <AccordionSummary
              // expandIcon={title !== "Benefits" ? <ExpandMoreIcon /> : null}
              aria-controls="panel1d-content"
              id="panel1d-header11"
              sx={{ paddingY: sizing(1), background: "#dbdbdb" }}
            >
              <Text sx={{ fontSize: "16px", color: "#6a6a6a" }} medium>
                Benefits
              </Text>
            </AccordionSummary>

            <AccordionDetails sx={{ background: "#dbdbdb", paddingBottom: "24px" }}>
              {benefits.map((a: any, index: any) => (
                <EasementRight key={index}>{a}</EasementRight>
              ))}
            </AccordionDetails>
          </Accordion>
        </>
      ) : (
        ""
      )}
    </Grid>
  );
};

const BidDialog = styled(MuiDialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "transparent",
    borderRadius: 18,
    border: "none",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
      margin: 12,
      maxWidth: 319,
      width: 319,
      height: 565,
      maxHeight: 565,
    },
    [theme.breakpoints.up("md")]: {
      maxWidth: 400,
      width: 400,
      minHeight: 600,
    },
  },
}));

const BidPopupContainer = styled(Box)<{ isMobile: boolean }>(({ isMobile }) => ({
  position: "relative",
  width: isMobile ? 319 : 400,
  height: isMobile ? 565 : "auto",
  minHeight: isMobile ? 565 : 600,
  display: "flex",
  flexDirection: "column",
}));

const ImageSection = styled(Box)<{ isMobile: boolean }>(({ isMobile }) => ({
  height: isMobile ? 318 : 250,
  backgroundSize: "cover",
  backgroundPosition: "center",
  borderRadius: "18px 18px 0 0",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  padding: isMobile ? "30px 30px 20px 30px" : "30px",
  position: "relative",
}));

const EFTTitle = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 700,
  fontSize: 26,
  color: "white",
  lineHeight: "normal",
  margin: 0,
  marginBottom: 8,
});

const EFTId = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: 700,
  fontSize: 26,
  color: "white",
  lineHeight: "normal",
  margin: 0,
});

const ContentSection = styled(Box)<{ isMobile: boolean }>(({ isMobile }) => ({
  flex: 1,
  backgroundColor: "white",
  borderRadius: "0 0 18px 18px",
  padding: isMobile ? "30px 30px 30px 30px" : "30px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
}));

const BidInputContainer = styled(Box)({
  margin: "20px 0",
});

const BidInputField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    height: 67,
    borderRadius: 20,
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 16,
    paddingRight: 20,
    "& input": {
      textAlign: "center",
      color: "black",
      paddingRight: 0,
    },
    "& .MuiInputAdornment-root": {
      color: "black",
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 16,
      fontWeight: 400,
      "& .MuiTypography-root": {
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 16,
        fontWeight: 400,
      },
    },
    "& fieldset": {
      borderColor: "#999999",
    },
    "&:hover fieldset": {
      borderColor: "#999999",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#999999",
      borderWidth: 1,
    },
    "&.Mui-disabled": {
      "& .MuiInputAdornment-root": {
        color: "black",
      },
    },
  },
});

const StatsGrid = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginBottom: 20,
});

const StatItem = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const StatLabel = styled(Typography)({
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 14,
  color: "#999999",
  textTransform: "uppercase",
  fontWeight: 400,
});

const StatValue = styled(Typography)({
  fontFamily: "'IBM Plex Mono', monospace",
  fontSize: 14,
  color: "black",
  textTransform: "uppercase",
  fontWeight: 400,
  textAlign: "right",
});

const Divider = styled(Box)({
  width: "100%",
  height: 1,
  backgroundColor: "#E0E0E0",
  margin: "12px 0",
});

const ActionButtonNew = styled(Button)({
  height: 40,
  width: "100%",
  backgroundColor: "black",
  color: "white",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  textTransform: "none",
  border: "none",
  padding: "11px 0 12px 0",
  "&:hover": {
    backgroundColor: "#333",
    border: "none",
    color: "white",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    color: "rgba(255, 255, 255, 0.6)",
  },
});

const CancelButton = styled(Button)({
  height: 40,
  width: "100%",
  backgroundColor: "#333",
  color: "white",
  borderRadius: 20,
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 700,
  textTransform: "none",
  border: "none",
  "&:hover": {
    border: "none",
    backgroundColor: "#333",
  },
});

const BalanceText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  color: "#999999",
  textAlign: "center",
});

const ProcessingText = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  color: "#999999",
  textAlign: "center",
});

export const AuctionInput = styled("div")`
  margin-top: 12px;
  width: 100%;

  div {
    width: 100%;

    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    input {
      width: 100%;
      font-family: IBM Mono, monospace;
      font-weight: 400;
      font-size: 20px;
      padding-right: 96px;
    }
  }

  fieldset {
    height: 100%;
    top: 0;
    width: 100%;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.palette.border.default};
  }

  height: max-content;
  margin-bottom: 12px;

  display: flex;
  flex-direction: row;
  align-items: center;

  position: relative;

  span {
    display: block;
    position: absolute;
    right: 16px;
    user-select: none;
    font-size: 16px;
    font-family: IBM Mono, monospace;
  }
`;

export const Status = styled("div")`
  width: 100%;
  display: grid;
  grid-template-columns: 50% 50%;

  .currentBid > div,
  .currentTime > div {
    display: flex;

    &:first-of-type {
      font-size: 14px;
    }
  }

  & > div:last-of-type {
    padding-left: 24px;
    border-left: var(--border);
    @media (max-width: 900px) {
      padding-left: 12px;
    }
  }

  @media (max-width: 560px) {
    grid-template-columns: 100%;
    width: 100%;
    & > div:last-of-type {
      margin-top: 12px;
      padding-top: 12px;
      padding-left: 0;
      border-top: var(--border);
      border-left: none;
    }
  }
`;

export const EasementsWrap = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: start;
  width: 100%;

  background: ${({ theme }) => theme.palette.back.tiers};
  // border-bottom: 1px solid;
  // border-top: 1px solid;
  // border-color: ${({ theme }) => theme.palette.border.dark};
  color: black;
  h6 {
    font-family: inherit;
  }
`;

export const BasicPlan = styled("div")`
  width: 100%;
  padding-top: 24px;
  display: grid;
  grid-template-columns: 30% 70%;
  align-items: start;
  position: relative;
  border-top: 0.5px solid white;

  &.activated {
    &:after {
      content: "Activated";
      position: absolute;
      top: 5px;
      right: 10px;
      font-size: 10px;
      color: forestgreen;
    }
  }

  blockquote {
    margin: 10px 0;
  }

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    margin-top: 12px;
    & > div:first-of-type:not(:empty) {
      padding-bottom: 12px;
      margin-bottom: 12px;
      border-bottom: 1px solid;
      border-color: ${({ theme }) => theme.palette.border.dark};
    }
  }
`;

export const EasementRight = styled("div")`
  width: 100%;
  color: black;
  font-size: 16px;
`;

const Easements = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 12px auto 0;
`;

export const DialogPopUp = styled(MuiDialog)`
  .MuiDialog-paper {
    max-width: 600px;
    min-width: 300px;
    background: transparent;
    border-radius: 18px;
    //box-shadow: none;
    border: 1px solid transparent;
    @media (max-width: 560px) {
      // height:350px;
      margin: 12px;
      max-width: 325px;
    }
  }
`;

export default Auction;
